/* eslint-disable */
import React, { useState } from "react";
import {
  Button,
  Grid,
  Group,
  TextInput,
  Switch,
  Box,
  Text,
  Divider,
  ScrollArea,
  Stack,
  Title,
  Badge,
  ThemeIcon,
} from "@mantine/core";
import {
  IconUser,
  IconCalendar,
  IconUsers,
  IconChartBar,
  IconBuildingCommunity,
  IconSettings,
  IconKey,
  IconShield,
  IconBriefcase,
  IconClockEdit,
  IconTicket,
  IconLayoutDashboard,
  IconSun,
} from "@tabler/icons-react";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import { showNotification } from "@mantine/notifications";
import { ExclamationMark } from "tabler-icons-react";
import { useUpdateUserPermission } from "api/permission/getAllUsersAndPermissions";
import {
  getPermissionLabel,
  getPermissionCategoryLabel,
} from "utils/permissionLabels";
import { getPermissionDescription } from "utils/permissionDescriptions";
import { getSortedPermissions } from "utils/permissionSorter";
import { mergeWithPermissionSkeleton } from "utils/permissionSchema";
import { AdminPermissionScopesModal } from "features/admin-settings/components/OrgMembersTable/ActionsBtn/EditRoleModal/AdminPermissionScopesModal";
import { PermissionScopeBadge } from "features/admin-settings/components/OrgMembersTable/ActionsBtn/EditRoleModal/PermissionScopeBadge";
const permissionSchema: z.ZodType<any> = z.lazy(() =>
  z.union([
    z.boolean(),
    z.record(permissionSchema), // recursive
  ])
);
// Validation schema - only validate permissions since other fields are disabled
const addUserSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  role: z.string().optional(),
  permissions: z.record(permissionSchema),
});

type FormValues = z.infer<typeof addUserSchema>;

interface UserData {
  userId: {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    _id: string;
  };
  role: string;
  permissions?: Record<string, boolean | Record<string, boolean>>;
}

interface EditFormUserProps {
  onClose: () => void;
  userData: UserData;
}

// Category icons mapping
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  userProfile: <IconUser size={14} />,
  attendance: <IconCalendar size={14} />,
  employees: <IconUsers size={14} />,
  reports: <IconChartBar size={14} />,
  department: <IconBuildingCommunity size={14} />,
  organization_management: <IconSettings size={14} />,
  permission_management: <IconKey size={14} />,
  role_management: <IconShield size={14} />,
  employment_history: <IconBriefcase size={14} />,
  configuration_management: <IconClockEdit size={14} />,
  program_management: <IconBriefcase size={14} />,
  holiday_managment: <IconSun size={14} />,
  ticket: <IconTicket size={14} />,
  dashboardAccess: <IconLayoutDashboard size={14} />,
  user_management: <IconUsers size={14} />,
};

// Helper to count enabled permissions in a group
const countEnabled = (obj: Record<string, any>): number =>
  Object.values(obj).filter((v) => v === true).length;

export default function EditFormUser({ onClose, userData }: EditFormUserProps) {
  const { mutate, isLoading } = useUpdateUserPermission();

  // State for scope modal
  const [scopeModalOpened, setScopeModalOpened] = useState(false);
  const [enabledAdminPermissions, setEnabledAdminPermissions] = useState<Array<'attendance.admin' | 'employees.adminLeave'>>([]);
  const [adminPermissionScopes, setAdminPermissionScopes] = useState<Record<'attendance.admin' | 'employees.adminLeave', 'department' | 'program' | 'user'>>(
    {} as Record<'attendance.admin' | 'employees.adminLeave', 'department' | 'program' | 'user'>
  )

  // Overlay the member's saved permissions on the full skeleton so newly-added
  // permission keys (e.g. reports.*Import / view_audit_logs) still appear as
  // toggles even when the member's stored doc predates them.
  const sortedPermissions = getSortedPermissions(
    mergeWithPermissionSkeleton(userData.permissions ?? {}) as unknown as Record<
      string,
      boolean | Record<string, boolean>
    >
  );

  const form = useForm<FormValues>({
    initialValues: {
      firstName: userData.userId.firstName,
      lastName: userData.userId.lastName,
      email: userData.userId.email,
      role: userData.role,
      permissions: sortedPermissions,
    },
    validate: zodResolver(addUserSchema),
  });

  // Check for admin permissions in the permission object
  const getEnabledAdminPermissions = (permissions: Record<string, any>): Array<'attendance.admin' | 'employees.adminLeave'> => {
    const adminPerms: Array<'attendance.admin' | 'employees.adminLeave'> = [];

    if (permissions?.attendance?.admin === true) {
      adminPerms.push('attendance.admin');
    }
    if (permissions?.employees?.adminLeave === true) {
      adminPerms.push('employees.adminLeave');
    }

    return adminPerms;
  };

  // Handle permission change - show modal if admin permission is toggled
  const handlePermissionChange = (permissionPath: string, value: boolean) => {
    form.setFieldValue(permissionPath, value);

    // Check if this is an admin permission being enabled
    if (value &&
      (permissionPath === 'permissions.attendance.admin' ||
       permissionPath === 'permissions.employees.adminLeave')
    ) {
      // Get current state after update
      const updatedPerms = JSON.parse(JSON.stringify(form.values.permissions));

      // Parse path and update the value
      const pathParts = permissionPath.replace('permissions.', '').split('.');
      let current = updatedPerms;
      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }
      current[pathParts[pathParts.length - 1]] = value;

      // Show modal for scope selection
      const adminPerms = getEnabledAdminPermissions(updatedPerms);

      if (adminPerms.length > 0) {
        setEnabledAdminPermissions(adminPerms);
        setScopeModalOpened(true);
      }
    }
  };

  const handleSubmit = (values: FormValues) => {
    console.log("✅ handleSubmit called");
    console.log("Form values:", values);

    const dataToSubmit = {
      ...values.permissions,
    };

    // Add scopes if any
    if (Object.keys(adminPermissionScopes).length > 0) {
      dataToSubmit.adminPermissionScopes = adminPermissionScopes;
    }

    const finalPayload = {
      id: userData?.userId?._id,
      data: { permissions: dataToSubmit },
    };

    console.log("📤 Sending payload:", finalPayload);

    mutate(finalPayload, {
      onSuccess: () => {
        console.log("✅ Success! Permissions updated");
        onClose();
      },
      onError: (error) => {
        console.error("❌ Error updating permissions:", error);
        showNotification({
          title: "Error",
          message: "Failed to update permissions. Please try again.",
          color: "red",
          icon: <ExclamationMark />,
        });
      },
    });
  };

  const handleScopeConfirm = (scopes: Record<'attendance.admin' | 'employees.adminLeave', 'department' | 'program' | 'user'>) => {
    setAdminPermissionScopes(scopes);
    setScopeModalOpened(false);
  };
  return (
    <>
      <AdminPermissionScopesModal
        opened={scopeModalOpened}
        onClose={() => setScopeModalOpened(false)}
        onConfirm={handleScopeConfirm}
        enabledPermissions={enabledAdminPermissions}
        existingScopes={adminPermissionScopes}
        isLoading={isLoading}
      />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing="md">
          <Title order={4}>User Details</Title>

        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="First Name"
              {...form.getInputProps("firstName")}
              disabled
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Last Name"
              {...form.getInputProps("lastName")}
              disabled
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Email"
              {...form.getInputProps("email")}
              disabled
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput label="Role" {...form.getInputProps("role")} disabled />
          </Grid.Col>
        </Grid>

        {/* PERMISSIONS SECTION */}
        <Box>
          <Text weight={700} mb={10}>
            Permissions
          </Text>

          <ScrollArea style={{ height: 480, overflow: "auto" }}>
            <Box sx={{ paddingRight: 16 }}>
              {/* TOP-LEVEL BOOLEAN PERMISSIONS - 2 per row card style */}
              {Object.entries(form.values.permissions).filter(
                ([_, value]) => typeof value === "boolean"
              ).length > 0 && (
                <Grid mb="lg" gutter="xs">
                  {Object.entries(form.values.permissions)
                    .filter(([_, value]) => typeof value === "boolean")
                    .map(([key, value]) => {
                      const boolValue = value as boolean;
                      const label = getPermissionLabel(key);
                      const description = getPermissionDescription(key);

                      return (
                        <Grid.Col span={6} key={key}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "10px 12px",
                              backgroundColor: "#fff",
                              borderRadius: 8,
                              border: "1px solid #e9ecef",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderColor: "#d9dbe3",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                              },
                            }}
                          >
                            <Box sx={{ flex: 1, marginRight: 12 }}>
                              <Text size="sm" weight={600}>
                                {label}
                              </Text>
                              {description && (
                                <Text size="xs" color="dimmed" mt={2}>
                                  {description}
                                </Text>
                              )}
                            </Box>
                            <Switch
                              checked={boolValue}
                              onChange={(e) =>
                                handlePermissionChange(
                                  `permissions.${key}`,
                                  e.currentTarget.checked
                                )
                              }
                              size="sm"
                            />
                          </Box>
                        </Grid.Col>
                      );
                    })}
                </Grid>
              )}

              {/* NESTED PERMISSION GROUPS - Card based layout */}
              {Object.entries(form.values.permissions)
                .filter(
                  ([_, value]) => typeof value === "object" && value !== null
                )
                .map(([key, value]) => {
                  const categoryLabel = getPermissionCategoryLabel(key);
                  const categoryIcon = CATEGORY_ICONS[key] || <IconShield size={14} />;
                  const objectValue = value as Record<string, any>;
                  const enabledCount = countEnabled(objectValue);
                  const totalCount = Object.keys(objectValue).length;

                  return (
                    <Box
                      key={key}
                      mb="sm"
                      sx={{
                        border: "1px solid #dee2e6",
                        borderRadius: 10,
                        overflow: "hidden",
                        backgroundColor: "#fff",
                      }}
                    >
                      {/* Category Header */}
                      <Group
                        position="apart"
                        p="8px 12px"
                        sx={{
                          backgroundColor: "#f8f9fa",
                          borderBottom: "1px solid #dee2e6",
                        }}
                      >
                        <Group spacing={6}>
                          <ThemeIcon
                            variant="light"
                            color="orange"
                            size="sm"
                            radius="sm"
                          >
                            {categoryIcon}
                          </ThemeIcon>
                          <Text size="sm" weight={700}>
                            {categoryLabel}
                          </Text>
                        </Group>
                        <Badge
                          variant="filled"
                          size="sm"
                          color={enabledCount > 0 ? "orange" : "gray"}
                        >
                          {enabledCount}/{totalCount}
                        </Badge>
                      </Group>

                      {/* Permission Items */}
                      <Box sx={{ backgroundColor: "#fff" }}>
                        {Object.entries(objectValue).map(([subKey, subValue], idx, arr) => {
                          // Handle nested objects (rarely used, but keeping support)
                          if (typeof subValue === "object" && subValue !== null) {
                            return (
                              <Box key={`${key}.${subKey}`} p="sm" sx={{ borderBottom: "1px solid #f1f3f5" }}>
                                <Text size="sm" weight={500} mb="xs" color="dimmed">
                                  {getPermissionLabel(`${key}.${subKey}`)}
                                </Text>
                                <Stack spacing="xs" pl="md">
                                  {Object.entries(subValue).map(([nestedKey, nestedValue]) => (
                                    <Group
                                      key={`${key}.${subKey}.${nestedKey}`}
                                      position="apart"
                                    >
                                      <Box sx={{ flex: 1 }}>
                                        <Text size="sm" weight={500}>
                                          {getPermissionLabel(`${key}.${subKey}.${nestedKey}`)}
                                        </Text>
                                        <Text size="xs" color="dimmed" mt={1}>
                                          {getPermissionDescription(
                                            `${key}.${subKey}.${nestedKey}`
                                          )}
                                        </Text>
                                      </Box>
                                      <Switch
                                        checked={!!nestedValue}
                                        onChange={(e) =>
                                          handlePermissionChange(
                                            `permissions.${key}.${subKey}.${nestedKey}`,
                                            e.currentTarget.checked
                                          )
                                        }
                                        size="sm"
                                      />
                                    </Group>
                                  ))}
                                </Stack>
                              </Box>
                            );
                          }

                          // Standard permission item
                          const permLabel = getPermissionLabel(`${key}.${subKey}`);
                          const permDescription = getPermissionDescription(
                            `${key}.${subKey}`
                          );
                          const isLast = idx === arr.length - 1;

                          return (
                            <Group
                              key={`${key}.${subKey}`}
                              position="apart"
                              grow
                              spacing="sm"
                              sx={{
                                padding: "10px 12px",
                                borderBottom: "1px solid #f1f3f5",
                                transition: "background-color 0.1s ease",
                                "&:hover": {
                                  backgroundColor: "#f9fafb",
                                },
                                "&:last-child": {
                                  borderBottom: "none",
                                },
                              }}
                            >
                              <Box>
                                <Text size="sm" weight={500}>
                                  {permLabel}
                                </Text>
                                {permDescription && (
                                  <Text size="xs" color="dimmed" mt={1}>
                                    {permDescription}
                                  </Text>
                                )}
                              </Box>
                              <Switch
                                checked={!!subValue}
                                onChange={(e) =>
                                  handlePermissionChange(
                                    `permissions.${key}.${subKey}`,
                                    e.currentTarget.checked
                                  )
                                }
                                size="sm"
                              />
                            </Group>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </ScrollArea>
        </Box>

        {/* Display Current Permission Scopes */}
        {adminPermissionScopes && Object.keys(adminPermissionScopes).length > 0 && (
          <>
            <Box mt="md" p="md" sx={{ border: '1px solid #e9ecef', borderRadius: 8, backgroundColor: '#f9fafb' }}>
              <Text weight={600} size="sm" mb="sm">
                Admin Permission Scopes
              </Text>
              <Stack spacing="xs">
                {(['attendance.admin', 'employees.adminLeave'] as const).map((perm) => {
                  if (adminPermissionScopes[perm]) {
                    return (
                      <Group key={perm} position="apart">
                        <Text size="sm">
                          {perm === 'attendance.admin' ? 'Attendance Admin' : 'Admin Leave'}
                        </Text>
                        <PermissionScopeBadge
                          permission={perm}
                          scope={adminPermissionScopes[perm]}
                          onEdit={() => {
                            setEnabledAdminPermissions([perm]);
                            setScopeModalOpened(true);
                          }}
                        />
                      </Group>
                    );
                  }
                  return null;
                })}
              </Stack>
            </Box>
          </>
        )}

        <Divider my="md" />

        <Group position="right">
          <Button color="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="orange"
            disabled={isLoading}
            onClick={() => {
              console.log("🔘 Submit button clicked");
              form.validate();
              const errors = form.errors;
              console.log("📋 Form errors:", errors);

              if (Object.keys(errors).length === 0) {
                console.log("✔️ Form is valid, calling handleSubmit");
                handleSubmit(form.values);
              } else {
                console.warn("❌ Form has validation errors:", Object.keys(errors));
              }
            }}
          >
            Update Permissions
          </Button>
        </Group>
      </Stack>
    </form>
    </>
  );
}
